import React from "react";
import {
    Box,
    Flex,
    Heading,
    Text,
    Container,
    Button,
    HStack,
    Icon,
    SimpleGrid
} from "@chakra-ui/react";
import Slider from "react-slick";
import { Link as RouterLink } from 'react-router-dom';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaPhoneAlt, FaArrowRight, FaShieldAlt, FaAward, FaUserCheck } from "react-icons/fa";
import { GiScales } from "react-icons/gi";
import { useLanguage } from "../../context/LanguageContext";

const HeroCarousel = () => {
    const { t } = useLanguage();

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 700,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5500,
        fade: true,
        arrows: false,
    };

    const bgImages = [
        "/assets/images/homePage/hero.webp",
        "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80",
        "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1600&q=80"
    ];

    const slides = [
        {
            title: t("hero.slides.0.title"),
            description: t("hero.slides.0.description"),
            bgImage: bgImages[0]
        },
        {
            title: t("hero.slides.1.title"),
            description: t("hero.slides.1.description"),
            bgImage: bgImages[1]
        },
        {
            title: t("hero.slides.2.title"),
            description: t("hero.slides.2.description"),
            bgImage: bgImages[2]
        }
    ];

    const stats = [
        { val: "5+", label: t("hero.stats.yearsExp"), icon: FaAward },
        { val: "200+", label: t("hero.stats.cases"), icon: GiScales },
        { val: "94%", label: t("hero.stats.successRate"), icon: FaUserCheck },
        { val: "100%", label: t("hero.stats.confidentiality"), icon: FaShieldAlt },
    ];

    return (
        <Box as="section" position="relative" overflow="hidden">
            <Slider {...sliderSettings}>
                {slides.map((item, idx) => (
                    <Box
                        key={idx}
                        position="relative"
                        minH={{ base: "520px", md: "620px", lg: "680px" }}
                        display="flex !important"
                        alignItems="center"
                    >
                        {/* Background with Dark Cinematic Overlay */}
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            bgImage={`url('${item.bgImage}')`}
                            bgSize="cover"
                            bgPosition="center"
                            _after={{
                                content: '""',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: "linear-gradient(90deg, rgba(7, 13, 30, 0.94) 0%, rgba(11, 19, 43, 0.85) 55%, rgba(11, 19, 43, 0.45) 100%)",
                            }}
                        />

                        {/* Ambient decorative glow */}
                        <Box
                            position="absolute"
                            top="20%"
                            left="10%"
                            w="400px"
                            h="400px"
                            bg="radial-gradient(circle, rgba(223, 186, 115, 0.12) 0%, transparent 70%)"
                            pointerEvents="none"
                        />

                        <Container maxW="container.xl" px={{ base: 4, md: 8 }} position="relative" zIndex={2} py={{ base: 12, md: 16 }}>
                            <Box maxW={{ base: "100%", md: "780px" }} textAlign={{ base: "center", md: "left" }}>

                                {/* Main Heading */}
                                <Heading
                                    as="h1"
                                    fontSize={{ base: "28px", sm: "36px", md: "48px", lg: "54px" }}
                                    fontWeight="800"
                                    color="white"
                                    lineHeight={{ base: "1.25", md: "1.18" }}
                                    letterSpacing="-0.02em"
                                    mb={5}
                                >
                                    {item.title}
                                </Heading>

                                {/* Description */}
                                <Text
                                    fontSize={{ base: "md", md: "lg", lg: "xl" }}
                                    color="gray.300"
                                    maxW="680px"
                                    lineHeight="1.7"
                                    mb={8}
                                >
                                    {item.description}
                                </Text>

                                {/* Dual CTAs */}
                                <HStack
                                    spacing={{ base: 3, sm: 4 }}
                                    mb={10}
                                    justify={{ base: "center", md: "flex-start" }}
                                    flexWrap="wrap"
                                >
                                    <Button
                                        as={RouterLink}
                                        to="/contact"
                                        size={{ base: "md", md: "lg" }}
                                        bg="linear-gradient(135deg, #dfba73 0%, #c5a880 100%)"
                                        color="#0b132b"
                                        fontWeight="800"
                                        px={{ base: 6, md: 8 }}
                                        borderRadius="xl"
                                        boxShadow="0 10px 25px rgba(223, 186, 115, 0.35)"
                                        _hover={{
                                            transform: "translateY(-3px)",
                                            boxShadow: "0 15px 30px rgba(223, 186, 115, 0.5)",
                                        }}
                                        rightIcon={<FaArrowRight />}
                                        transition="all 0.3s ease"
                                    >
                                        {t("hero.btnConsultation")}
                                    </Button>

                                    <Button
                                        as={RouterLink}
                                        to="/services"
                                        size={{ base: "md", md: "lg" }}
                                        variant="outline"
                                        color="white"
                                        borderColor="rgba(255, 255, 255, 0.3)"
                                        borderRadius="xl"
                                        px={{ base: 6, md: 8 }}
                                        bg="whiteAlpha.100"
                                        backdropFilter="blur(10px)"
                                        _hover={{
                                            bg: "whiteAlpha.250",
                                            borderColor: "white",
                                            transform: "translateY(-3px)",
                                        }}
                                        leftIcon={<FaPhoneAlt fontSize="13px" />}
                                        transition="all 0.3s ease"
                                    >
                                        {t("hero.btnServices")}
                                    </Button>
                                </HStack>

                                {/* Trust Metrics Floating Bar */}
                                <SimpleGrid
                                    columns={{ base: 2, sm: 4 }}
                                    spacing={4}
                                    pt={4}
                                    borderTop="1px solid rgba(255, 255, 255, 0.1)"
                                    maxW="680px"
                                >
                                    {stats.map((stat, i) => (
                                        <HStack key={i} spacing={2.5}>
                                            <Flex
                                                w={8}
                                                h={8}
                                                borderRadius="lg"
                                                bg="rgba(223, 186, 115, 0.15)"
                                                align="center"
                                                justify="center"
                                                color="#dfba73"
                                            >
                                                <Icon as={stat.icon} fontSize="14px" />
                                            </Flex>
                                            <Box>
                                                <Text color="white" fontWeight="800" fontSize={{ base: "md", md: "lg" }} lineHeight="1">
                                                    {stat.val}
                                                </Text>
                                                <Text color="gray.400" fontSize="11px" mt={0.5} lineHeight="1.2">
                                                    {stat.label}
                                                </Text>
                                            </Box>
                                        </HStack>
                                    ))}
                                </SimpleGrid>
                            </Box>
                        </Container>
                    </Box>
                ))}
            </Slider>
        </Box>
    );
};

export default HeroCarousel;